import React from 'react';

/**
 * Skeleton primitives — dark glass shimmer matched to the Ads Dashbound palette.
 *
 * Use these instead of generic Loader2 spinners when waiting on async data
 * for layout-heavy screens (blog list, admin tables, dashboards, etc.).
 *
 * All shimmer animation is CSS-only and honors `prefers-reduced-motion`.
 */

function cx(...c) {
  return c.filter(Boolean).join(' ');
}

/** Base shimmer block — width/height/rounded controlled by className. */
export function SkeletonBlock({ className = '', testId }) {
  return (
    <div
      data-testid={testId}
      className={cx('skeleton-shimmer rounded-md', className)}
      aria-hidden="true"
    />
  );
}

/** A glass card placeholder — dark with cyan/violet shimmer. */
export function SkeletonCard({ className = '', testId }) {
  return (
    <div
      data-testid={testId}
      aria-hidden="true"
      className={cx(
        'rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-md p-5 space-y-3 overflow-hidden relative',
        className,
      )}
    >
      <SkeletonBlock className="h-5 w-2/3" />
      <SkeletonBlock className="h-3 w-full" />
      <SkeletonBlock className="h-3 w-5/6" />
      <SkeletonBlock className="h-3 w-3/4" />
      <div className="pt-2 flex gap-2">
        <SkeletonBlock className="h-7 w-20 rounded-full" />
        <SkeletonBlock className="h-7 w-24 rounded-full" />
      </div>
    </div>
  );
}

/** Light variant for sections rendered on `bg-[#F5F7FB]` (slate light bg). */
export function SkeletonCardLight({ className = '', testId }) {
  return (
    <div
      data-testid={testId}
      aria-hidden="true"
      className={cx(
        'rounded-2xl border border-slate-900/10 bg-white shadow-[0_8px_24px_rgba(2,6,23,0.04)] p-5 space-y-3 overflow-hidden relative',
        className,
      )}
    >
      <SkeletonBlock className="h-5 w-2/3 skeleton-shimmer-light" />
      <SkeletonBlock className="h-3 w-full skeleton-shimmer-light" />
      <SkeletonBlock className="h-3 w-5/6 skeleton-shimmer-light" />
      <SkeletonBlock className="h-3 w-3/4 skeleton-shimmer-light" />
      <div className="pt-2 flex gap-2">
        <SkeletonBlock className="h-7 w-20 rounded-full skeleton-shimmer-light" />
        <SkeletonBlock className="h-7 w-24 rounded-full skeleton-shimmer-light" />
      </div>
    </div>
  );
}

/** A grid of glass card skeletons — useful for blog/feature/format lists. */
export function SkeletonGrid({ count = 6, light = false, className = '', testId = 'skeleton-grid' }) {
  const Card = light ? SkeletonCardLight : SkeletonCard;
  return (
    <div
      data-testid={testId}
      className={cx(
        'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5',
        className,
      )}
    >
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} />
      ))}
    </div>
  );
}

/** Table-row skeletons for admin tables. */
export function SkeletonTableRows({ rows = 5, columns = 9, testId = 'skeleton-table-rows' }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} data-testid={testId}>
          {Array.from({ length: columns }).map((__, c) => (
            <td key={c} className="px-4 py-3">
              <SkeletonBlock className={cx('h-3', c === 0 ? 'w-24' : c === 1 ? 'w-32' : 'w-full max-w-[120px]')} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
